import React, { useEffect, useState, useContext, useRef } from "react";
import { LoaderContext } from "../../../context/LoaderContext";
import { defaultModalProps, Modal } from "../ModalComponent/Modal";
import { ParseJWT, POST } from "../../../utils/httpUtils";
import { OTPInput } from "../../OTPInput/otp-input";
import ErrorPage from "../../../pages/ErrorPage/error-page";
import mfaIcon from "../../../assets/images/mfa.png";
import ModalDividerLine from "../ModalComponent/modal-divider-line";
import { useNavigate } from "react-router-dom";
import "./mfa-setup-modal.css";

const defaultProps = {
  ...defaultModalProps,
  onSuccess: () => {},
};

export function MFAModal(props = defaultProps) {
  const router = useNavigate();

  //refs
  const showOtp = useRef();
  const OTP_INPUT = useRef();
  const submit_button = useRef();
  const error_message = useRef();
  const local_user_object = useRef(null);
  const phone_input = useRef();
  //state
  const [selectedMFAMethod, setMFAMethod] = useState(null);
  const { show, showloader, hideloader } = useContext(LoaderContext);
  const [force, forceRender] = useState(false);

  useEffect(() => {}, [selectedMFAMethod, force]);

  const subtextA = "We need to verify that you own this number before we can add it to your account";
  let subTextB = "Which method would you like to use for MFA authentication?";

  if (selectedMFAMethod === "email") {
    subTextB = (
      <>
        We will use <strong>{local_user_object.current.email}</strong> to send you MFA authentication codes?
      </>
    );
  }
  if (selectedMFAMethod === "sms") subTextB = "Please provide a valid phone number to send you MFA authentication codes.";

  function RenderModal() {
    //check for existing auth
    if (localStorage.getItem("jwt") === null && sessionStorage.getItem("jwt") === null) {
      return <ErrorPage header="Unauthorized" errorMessage={"You need to be signed in with an active account to setup MFA."} />;
    } else {
      let localJWT = localStorage.getItem("jwt");
      let sessionJWT = sessionStorage.getItem("jwt");
      let JWT = localJWT ?? sessionJWT;
      local_user_object.current = ParseJWT(JWT);
      if (local_user_object.current === null)
        return <ErrorPage header="Unauthorized" errorMessage={"You need to be signed in with an active account to setup MFA."} />;
    }
    //show this modal after the user submits their preference
    if (showOtp.current === true) {
      return (
        <Modal
          id={props.id}
          showDivider={true}
          header="Verify Your Phone Number"
          message={subtextA}
          isActive={props.isActive === undefined ? true : props.isActive}
          animate={props.animate ?? false}
        >
          <OTPInput id={"mfa-verification-otp"} ref={OTP_INPUT} onKey={OnChar} />
          <div className="mfa-verification-controls">
            {ShowErrorMessage()}
            <button ref={submit_button} className="standard-button" onClick={SubmitCode}>
              Submit
            </button>
            <div className="modal-footnote-box">
              <p>didn't recieve a code?</p>
              <button onClick={ResendCode}>resend</button>
            </div>
          </div>
        </Modal>
      );
    } else {
      //default modal to show
      return (
        <Modal {...props} header={'Multi-Factor Authentication'} message={subTextB} showDivider={true} animate={props.animate} isActive={props.isActive === undefined ? true : props.isActive} icon={mfaIcon}>
          {ShowErrorMessage()}
          {/* Render Inputs*/}
          {RenderInputs()}
          {/* Render Controls */}
          <div className="mfa-modal-controls">{RenderOptions()}</div>
          {/* Render Cancel Button */}
          {/* {RenderCancelButton()} */}
          <ModalDividerLine showDivider={true} />
          <div className="modal-footnote-box">
            <p>I've Changed My Mind</p>
            <button
              onClick={() => {
                if (props.onCancel) props.onCancel();
                else {
                  router("/dashboard");
                }
              }}
            >
              Set Up Later
            </button>
          </div>
        </Modal>
      );
    }
  }

  function RenderInputs() {
    if (selectedMFAMethod === null) return null;
    if (selectedMFAMethod === "email") {
      return <div className="mfa-modal-input-box"></div>;
    }
    if (selectedMFAMethod === "sms") {
      return (
        <div className="mfa-modal-input-box">
          <p className="mfa-phone-input-label">Phone Number</p>
          <input
            id="mfa-phone-input"
            ref={phone_input}
            type="tel"
            name="phone input"
            placeholder="+1 555-555-5555"
            pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}"
          />
        </div>
      );
    }
  }

  function RenderOptions() {
    switch (selectedMFAMethod) {
      case null:
        return (
          <>
            <button
              className="standard-button"
              onClick={() => {
                setMFAMethod("email");
              }}
            >
              Send Via Email
            </button>
            <button
              className="standard-button"
              onClick={() => {
                setMFAMethod("sms");
              }}
            >
              Send Via SMS
            </button>
          </>
        );
      default:
        return (
          <>
            <button
              className="standard-button"
              onClick={() => {
                setMFAMethod(null);
              }}
            >
              Back
            </button>
            <button className="standard-button" onClick={SubmitMFAMethod} ref={submit_button}>
              Submit
            </button>
          </>
        );
    }
  }

  function GetValue() {
    return OTP_INPUT.current.GetValue();
  }

  function OnChar() {
    if (GetValue().length === 7) {
      submit_button.current.disabled = false;
    } else {
      submit_button.current.disabled = true;
    }
  }

  function ShowErrorMessage() {
    if (error_message.current) {
      return <p className="error-message">*{error_message.current}</p>;
    }
  }

  async function SubmitMFAMethod() {
    showloader();

    let mfa_config_payload = {
      method: selectedMFAMethod,
    };

    if (selectedMFAMethod === "sms") mfa_config_payload.phone = phone_input.current.value;

    let response = await POST("/mfa-config", mfa_config_payload, local_user_object.current);
    hideloader();
    if (response && response.success) {
      if (selectedMFAMethod === "sms") showOtp.current = true;
      else {
        if (props.onSuccess) props.onSuccess();
        else router("/dashboard");
      }
    } else {
      //show validation message
      error_message.current = response.error.message;
    }
  }

  async function SubmitCode() {
    showloader();
    let response = await POST("/verify-sms", { code: GetValue() }, local_user_object.current);
    hideloader();
    if (response.success) {
      if (props.onSuccess) props.onSuccess();
      else {
        router("/mfa-setup-success");
      }
    } else {
      //show validation message
      error_message.current = response.error.message;
    }
  }

  async function ResendCode() {
    showloader();
    let response = await POST("/resend-otp", { username: local_user_object.current.username, type: "verify_sms" });
    hideloader();
  }

  return RenderModal();
}
